import React, { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { DownloadIcon } from "../../components/icons";
import CfLoader from "../CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import renderPdf from "../../common/RenderPdf/RenderPdf";
import { detectFileType } from "../../utils/DetectFileType";
import Toast from "../../common/Toast/Toast";
import DownloadFile from "../../service/DownloadFile";
import {
  formStructureBuilder,
  formatFieldName,
} from "../../utils/formStructureBuilder";
import { useTranslation } from "react-i18next";

const VerificationApprovalCertificateModal = (props) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isPdfRendering, setIsPdfRendering] = useState(false);
  const pdfCanvasRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const { t } = useTranslation();

  const {
    isOpen,
    documentType,
    uploadedCertificateImage,
    certificateData,
    certificateId,
    openCloseModal,
    testId = "verification-approval-certificate-modal",
  } = props;

  const certificateDataStructure = useMemo(() => {
    if (certificateData && Object.keys(certificateData).length > 0) {
      return formStructureBuilder(certificateData);
    }
    return {};
  }, [certificateData]);

  const simpleFields = useMemo(() => {
    if (!certificateDataStructure) return [];
    return Object.entries(certificateDataStructure).filter(
      ([, config]) => config.type === "text" || config.type === "null",
    );
  }, [certificateDataStructure]);

  const complexFields = useMemo(() => {
    if (!certificateDataStructure) return [];
    return Object.entries(certificateDataStructure).filter(
      ([, config]) =>
        config.type === "arrayOfObjects" ||
        config.type === "simpleArray" ||
        config.type === "object",
    );
  }, [certificateDataStructure]);

  const renderSimpleFieldsPaired = () => {
    if (simpleFields.length === 0) return null;

    const fieldPairs = [];
    for (let i = 0; i < simpleFields.length; i += 2) {
      const field1 = simpleFields[i];
      const field2 = i + 1 < simpleFields.length ? simpleFields[i + 1] : null;
      fieldPairs.push([field1, field2]);
    }

    return (
      <div className="space-y-4 pr-2">
        {fieldPairs.map(([field1, field2], index) => (
          <div key={index} className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                {formatFieldName(field1[0])}
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {field1[1].value || t("verificationApprovals:na")}
              </div>
            </div>
            {field2 ? (
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-xs font-medium text-gray-500 uppercase  mb-1">
                  {formatFieldName(field2[0])}
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {field2[1].value || t("verificationApprovals:na")}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 p-3 rounded-md opacity-0">
                {/* Empty spacer to maintain layout */}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderComplexFields = () => {
    if (complexFields.length === 0) return null;

    return complexFields.map(([key, config]) => {
      const label = formatFieldName(key);

      if (config.type === "arrayOfObjects" && config.isArray) {
        return (
          <div key={key} className="space-y-3 mt-4">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
              {config.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className="p-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="space-y-2">
                    {config.fields.map((field) => (
                      <div key={field} className="flex justify-between">
                        <span className="text-xs text-gray-600 font-medium capitalize">
                          {formatFieldName(field)}:
                        </span>
                        <span className="text-xs text-gray-900 font-semibold text-right max-w-[60%] break-words">
                          {item[field] || t("verificationApprovals:na")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      } else if (config.type === "simpleArray" && config.isArray) {
        return (
          <div key={key} className="space-y-2 mt-4">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="flex flex-wrap gap-2">
              {config.items.map((item, itemIndex) => (
                <span
                  key={itemIndex}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        );
      } else if (config.type === "object" && !config.isArray) {
        return (
          <div key={key} className="space-y-3 mt-4">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
              {Object.entries(config.value).map(([nestedKey, nestedValue]) => (
                <div
                  key={nestedKey}
                  className="flex justify-between items-start"
                >
                  <span className="text-xs text-gray-600 font-medium capitalize">
                    {formatFieldName(nestedKey)}:
                  </span>
                  <span className="text-xs text-gray-900 font-semibold text-right max-w-[60%] break-words">
                    {nestedValue || t("verificationApprovals:na")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      return null;
    });
  };

  const renderCertificateDetails = () => {
    if (!certificateData || Object.keys(certificateData).length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {t("verificationApprovals:no_certificate_details_available")}
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-100 max-h-[70vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b flex items-center">
          {t("verificationApprovals:credential_subject_details")}
        </h3>

        {renderSimpleFieldsPaired()}

        {renderComplexFields()}
      </div>
    );
  };

  useEffect(() => {
    if (
      isOpen &&
      uploadedCertificateImage &&
      detectFileType(uploadedCertificateImage) === "pdf" &&
      pdfCanvasRef.current &&
      pdfContainerRef.current
    ) {
      const renderPdfWithScale = async () => {
        try {
          setIsPdfRendering(true);

          const container = pdfContainerRef.current;
          const containerWidth = container.clientWidth - 32;

          await renderPdf(
            uploadedCertificateImage,
            pdfCanvasRef,
            containerWidth,
          );

          // Adjust container height based on canvas content
          const canvas = pdfCanvasRef.current;
          if (canvas && canvas.height) {
            container.style.height = `${canvas.height}px`;
            container.style.overflow = "hidden";
          }
        } catch (error) {
          console.error("Error rendering PDF:", error);
        } finally {
          setIsPdfRendering(false);
        }
      };

      renderPdfWithScale();
    }
  }, [isOpen, uploadedCertificateImage]);

  const handleDownload = async () => {
    if (!uploadedCertificateImage) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:no_certificate_available_download"),
      });
      return;
    }

    setIsDownloading(true);
    try {
      const fileType = detectFileType(uploadedCertificateImage);

      if (documentType === "Data" || documentType === "Legacy") {
        const response = await fetch(uploadedCertificateImage);
        if (!response.ok) throw new Error("Failed to fetch file");

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = downloadUrl;

        let extension = "";
        switch (fileType.toLowerCase()) {
          case "pdf":
            extension = "pdf";
            break;
          case "image": {
            const urlParts = uploadedCertificateImage.split(".");
            const possibleExt = urlParts[urlParts.length - 1].toLowerCase();
            if (["png", "jpg", "jpeg", "gif", "webp"].includes(possibleExt)) {
              extension = possibleExt;
            } else {
              const contentType = response.headers.get("content-type");
              if (contentType?.includes("png")) extension = "png";
              else if (contentType?.includes("jpeg")) extension = "jpg";
              else extension = "png";
            }
            break;
          }
          default:
            extension = "";
        }
        const filename = `${
          certificateId || `${t("verificationApprovals:uploaded_certificate")}`
        }${extension ? `.${extension}` : ""}`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(link);
      } else if (documentType === "VC") {
        await DownloadFile(EntityConstants.DOWNLOAD_SVG_AS_PNG, certificateId);
      } else {
        const response = await fetch(uploadedCertificateImage);
        if (!response.ok) throw new Error("Failed to fetch image file");

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        let fileExtension = "png";
        if (uploadedCertificateImage.toLowerCase().includes(".png")) {
          fileExtension = "png";
        } else if (
          uploadedCertificateImage.toLowerCase().includes(".jpg") ||
          uploadedCertificateImage.toLowerCase().includes(".jpeg")
        ) {
          fileExtension = "jpg";
        } else if (uploadedCertificateImage.toLowerCase().includes(".gif")) {
          fileExtension = "gif";
        } else if (uploadedCertificateImage.toLowerCase().includes(".webp")) {
          fileExtension = "webp";
        }

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${t(
          "verificationApprovals:certificate",
        )}.${fileExtension}`;
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(link);
      }

      Toast({
        type: "success",
        title: t("verificationApprovals:success"),
        message: t("verificationApprovals:certificate_downloaded_successfully"),
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_download_certificate"),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const ImageLoading = (
    <div className="flex items-center justify-center h-64">
      <CfLoader />
    </div>
  );

  const fileType = detectFileType(uploadedCertificateImage);

  const hasCertificateData =
    certificateData && Object.keys(certificateData).length > 0;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => openCloseModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 top-20 bg-black/30 backdrop-blur-sm"
          />
        </Transition.Child>

        <div className="fixed inset-0 top-20 overflow-y-auto px-4 md:px-6 max-h-full">
          <div className="flex items-center min-h-full justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className={`w-full ${
                  hasCertificateData ? "max-w-7xl" : "max-w-4xl"
                } max-h-[85vh] transform overflow-auto rounded-2xl bg-white text-left align-middle shadow-xl transition-all`}
              >
                {/* Header */}
                <div className="flex justify-between items-center p-4 md:p-6 border-b bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {t("verificationApprovals:certificate_preview")}
                  </h2>
                  <div className="flex items-center gap-3">
                    <button
                      id={`${testId}-download`}
                      onClick={handleDownload}
                      disabled={isDownloading || !uploadedCertificateImage}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium ${
                        isDownloading || !uploadedCertificateImage
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-cfPrimaryButton hover:text-cfPrimaryDark"
                      } focus:outline-none`}
                      title="Download certificate"
                    >
                      <DownloadIcon size={16} className="inline-flex" aria-hidden />
                      <span>{t("verificationApprovals:download")}</span>
                    </button>
                    <button
                      type="button"
                      id={`${testId}-close`}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => openCloseModal(false)}
                    >
                      <span className="sr-only">Close</span>
                      &#10005;
                    </button>
                  </div>
                </div>

                <div className={`${hasCertificateData ? "p-4 h-full" : ""}`}>
                  <div
                    className={
                      hasCertificateData
                        ? "grid grid-cols-1 lg:grid-cols-2 gap-6"
                        : ""
                    }
                  >
                    {hasCertificateData && renderCertificateDetails()}

                    <div className="space-y-4">
                      <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                        {uploadedCertificateImage ? (
                          <div className="flex flex-col items-center p-4">
                            {fileType === "image" && (
                              <div className="flex items-center justify-center w-full">
                                {isImageLoading && ImageLoading}
                                <img
                                  src={uploadedCertificateImage}
                                  alt="Certificate"
                                  className={`max-h-[500px] max-w-full object-contain ${
                                    isImageLoading ? "hidden" : "block"
                                  }`}
                                  onLoad={() => setIsImageLoading(false)}
                                  onError={() => setIsImageLoading(false)}
                                />
                              </div>
                            )}
                            {fileType === "pdf" && (
                              <div className="w-full">
                                {isPdfRendering && ImageLoading}
                                <div
                                  ref={pdfContainerRef}
                                  className="w-full flex justify-center overflow-auto max-h-[500px] bg-gray-50 rounded-lg"
                                >
                                  <canvas
                                    ref={pdfCanvasRef}
                                    className={`max-w-full h-auto ${
                                      isPdfRendering ? "hidden" : "block"
                                    }`}
                                    style={{
                                      maxWidth: "100%",
                                      height: "auto",
                                      display: isPdfRendering
                                        ? "none"
                                        : "block",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            {fileType === "unsupported" && (
                              <div className="flex items-center justify-center w-full py-8">
                                <div className="text-center">
                                  <svg
                                    className="w-12 h-12 mx-auto text-gray-300 mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                  </svg>
                                  <p className="text-gray-500">
                                    {t(
                                      "verificationApprovals:preview_not_available_file_type",
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center min-h-[500px]">
                            <div className="text-center py-12">
                              <svg
                                className="w-16 h-16 mx-auto text-gray-300 mb-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                              <p className="text-gray-500 text-lg font-medium">
                                {t(
                                  "verificationApprovals:no_certificate_available",
                                )}
                              </p>
                              <p className="text-gray-400 text-sm mt-2">
                                {t(
                                  "verificationApprovals:upload_certificate_preview",
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default VerificationApprovalCertificateModal;
